import React from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Check, Star, Clock, Target, Dumbbell } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';

// Comprehensive workout types for SEO targeting
const workoutTypes = {
  'weight-loss': {
    title: 'Weight Loss Programs',
    h1: 'AI-Powered Weight Loss Workout Plans',
    description: 'Personalized weight loss workout programs designed by AI. Burn fat, build lean muscle, and achieve sustainable results with VYTA\'s science-backed approach.',
    keywords: 'weight loss workout plan, fat burning exercises, weight loss program, lose weight fast, belly fat workout, weight loss app, best weight loss program 2025',
    benefits: ['Calorie-burning workouts tailored to your fitness level', 'Nutrition plans to support fat loss', 'Progressive training to boost metabolism', 'Track your weight loss journey'],
    features: ['HIIT and cardio combinations', 'Strength training for lean muscle', 'Rest day optimization', 'Macro-calculated meal plans'],
  },
  'muscle-building': {
    title: 'Muscle Building Programs',
    h1: 'AI-Powered Muscle Building & Strength Training',
    description: 'Build muscle and gain strength with personalized AI training programs. Hypertrophy-focused workouts, progressive overload, and nutrition for muscle growth.',
    keywords: 'muscle building program, strength training plan, hypertrophy workout, gain muscle, bodybuilding program, build muscle fast, weight training program',
    benefits: ['Progressive overload programming', 'Muscle group split optimization', 'Recovery and rest day planning', 'High-protein nutrition plans'],
    features: ['Compound and isolation exercises', 'Volume and intensity periodization', 'Deload week scheduling', 'Protein-optimized meal plans'],
  },
  'marathon-training': {
    title: 'Marathon Training Plans',
    h1: 'AI Marathon Training Programs - 5K to Ultra',
    description: 'Train for your marathon with AI-generated periodized running programs. From couch to 5K, half marathon, full marathon, to ultra distances.',
    keywords: 'marathon training plan, running program, half marathon training, 5K training plan, ultra marathon, running app, marathon preparation',
    benefits: ['Periodized training peaking at race day', 'Pace and heart rate zone training', 'Injury prevention protocols', 'Race-day nutrition strategies'],
    features: ['Long run progression', 'Speed work and intervals', 'Tapering protocols', 'Carb-loading meal plans'],
  },
  'hyrox-training': {
    title: 'Hyrox Training Programs',
    h1: 'AI-Powered Hyrox Competition Training',
    description: 'Prepare for Hyrox with specialized AI training programs. Master all 8 workout stations with periodized functional fitness training.',
    keywords: 'Hyrox training plan, Hyrox workout, functional fitness, Hyrox preparation, Hyrox competition, Hyrox exercises, Hyrox program',
    benefits: ['All 8 Hyrox station preparation', 'Running endurance building', 'Functional strength development', 'Competition day strategy'],
    features: ['Ski erg technique', 'Sled push/pull training', 'Burpee broad jump drills', 'Wall ball and lunges'],
  },
  'crossfit': {
    title: 'CrossFit-Style Training',
    h1: 'AI CrossFit-Style Functional Fitness Programs',
    description: 'High-intensity functional fitness workouts inspired by CrossFit methodology. Constantly varied, functional movements at high intensity.',
    keywords: 'CrossFit workout, WOD, functional fitness, CrossFit training, HIIT workout, CrossFit program, CrossFit alternative',
    benefits: ['Varied daily workouts (WODs)', 'Olympic lifting progressions', 'Gymnastics skill development', 'Metabolic conditioning'],
    features: ['AMRAP and EMOM workouts', 'Benchmark WODs', 'Skill progressions', 'Competition prep'],
  },
  'home-workout': {
    title: 'Home Workout Programs',
    h1: 'AI Home Workout Plans - No Equipment Needed',
    description: 'Effective home workout programs that require no gym or equipment. Bodyweight exercises, minimal space, maximum results.',
    keywords: 'home workout plan, bodyweight exercises, no equipment workout, home fitness, workout at home, apartment workout, no gym workout',
    benefits: ['No equipment required', 'Small space friendly', 'Quiet exercise options', 'Family-friendly scheduling'],
    features: ['Bodyweight strength training', 'Cardio without running', 'Core and flexibility', 'Quick 20-minute options'],
  },
  'strength-training': {
    title: 'Strength Training Programs',
    h1: 'AI Strength Training & Powerlifting Programs',
    description: 'Build serious strength with AI-optimized powerlifting and strength programs. Progressive overload for squat, bench, and deadlift.',
    keywords: 'strength training program, powerlifting plan, get stronger, squat program, bench press program, deadlift program, 5x5 workout',
    benefits: ['Progressive overload optimization', 'Compound lift focus', 'Accessory work programming', 'Deload and recovery planning'],
    features: ['Squat, bench, deadlift focus', '5x5 and periodization', 'Accessory movements', 'Max testing protocols'],
  },
  'hiit': {
    title: 'HIIT Workout Programs',
    h1: 'AI High-Intensity Interval Training (HIIT) Programs',
    description: 'Burn maximum calories in minimum time with AI-designed HIIT workouts. Short, intense sessions for busy schedules.',
    keywords: 'HIIT workout, high intensity interval training, HIIT program, Tabata workout, fat burning HIIT, quick workout, 20 minute workout',
    benefits: ['Maximum calorie burn', 'Time-efficient workouts', 'Metabolism boosting', 'Cardiovascular improvement'],
    features: ['Tabata protocols', 'Work-rest optimization', '15-30 minute sessions', 'Equipment and bodyweight options'],
  },
  'yoga-flexibility': {
    title: 'Yoga & Flexibility Programs',
    h1: 'AI Yoga and Flexibility Training Programs',
    description: 'Improve flexibility, mobility, and mindfulness with AI-curated yoga and stretching programs for all levels.',
    keywords: 'yoga program, flexibility training, stretching routine, mobility workout, yoga for beginners, improve flexibility, yoga app',
    benefits: ['Improved flexibility and range of motion', 'Stress reduction', 'Better posture', 'Injury prevention'],
    features: ['Vinyasa flows', 'Static stretching', 'Mobility drills', 'Meditation integration'],
  },
  'senior-fitness': {
    title: 'Senior Fitness Programs',
    h1: 'AI Fitness Programs for Seniors & Active Aging',
    description: 'Safe, effective fitness programs designed for seniors and active aging. Low-impact exercises for strength, balance, and longevity.',
    keywords: 'senior fitness, exercises for seniors, over 50 workout, active aging, low impact exercise, senior strength training, balance exercises',
    benefits: ['Low-impact joint-friendly exercises', 'Balance and fall prevention', 'Bone density improvement', 'Social and mental benefits'],
    features: ['Chair exercises', 'Walking programs', 'Resistance band workouts', 'Balance training'],
  },
  'postpartum': {
    title: 'Postpartum Fitness Programs',
    h1: 'AI Postpartum Recovery & Fitness Programs',
    description: 'Safe postpartum fitness programs to help new mothers regain strength, heal properly, and return to fitness after pregnancy.',
    keywords: 'postpartum workout, post pregnancy exercise, diastasis recti exercises, new mom workout, postnatal fitness, after baby workout',
    benefits: ['Safe core rehabilitation', 'Pelvic floor strengthening', 'Gradual return to fitness', 'Time-flexible for new moms'],
    features: ['Diastasis recti healing', 'Pelvic floor exercises', 'Low-impact cardio', 'Baby-friendly workout times'],
  },
  'triathlon': {
    title: 'Triathlon Training Programs',
    h1: 'AI Triathlon Training - Sprint to Ironman',
    description: 'Comprehensive triathlon training programs for swim, bike, run. From sprint distance to full Ironman preparation.',
    keywords: 'triathlon training plan, Ironman training, swim bike run, triathlon program, sprint triathlon, Olympic triathlon, triathlon app',
    benefits: ['Three-sport integration', 'Brick workout scheduling', 'Race-specific periodization', 'Transition practice'],
    features: ['Swim technique drills', 'Bike power training', 'Run off the bike', 'Nutrition for endurance'],
  },
};

export default function WorkoutPrograms() {
  const navigate = useNavigate();
  const { type } = useParams();
  
  const workout = type ? workoutTypes[type] : null;

  // If specific workout type
  if (workout) {
    return (
      <>
        <SEO 
          title={workout.title}
          description={workout.description}
          keywords={workout.keywords}
          url={`https://vyta.com/programs/${type}`}
        />
        <div className="min-h-screen bg-black text-white">
          <header className="border-b border-zinc-800">
            <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
              <Link to="/" className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-xl bg-[#CCFF00] flex items-center justify-center">
                  <span className="text-black font-black text-xl">V</span>
                </div>
                <span className="font-bold text-xl">VYTA</span>
              </Link>
              <Button onClick={() => navigate('/register')} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold rounded-full">
                Start Free Trial
              </Button>
            </div>
          </header>

          <main className="max-w-4xl mx-auto px-6 py-16">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <h1 className="text-4xl sm:text-5xl font-black mb-6">{workout.h1}</h1>
              <p className="text-xl text-zinc-400 mb-12">{workout.description}</p>

              <div className="grid md:grid-cols-2 gap-8 mb-12">
                <div className="p-6 rounded-2xl bg-zinc-900/50 border border-zinc-800">
                  <h2 className="text-xl font-bold mb-4 text-[#CCFF00]">Benefits</h2>
                  <ul className="space-y-3">
                    {workout.benefits.map((b, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <Check className="w-5 h-5 text-[#CCFF00] flex-shrink-0 mt-0.5" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="p-6 rounded-2xl bg-zinc-900/50 border border-zinc-800">
                  <h2 className="text-xl font-bold mb-4 text-[#14B8A6]">Program Features</h2>
                  <ul className="space-y-3">
                    {workout.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <Dumbbell className="w-5 h-5 text-[#14B8A6] flex-shrink-0 mt-0.5" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="text-center p-8 rounded-2xl bg-gradient-to-r from-[#CCFF00]/10 to-[#14B8A6]/10 border border-[#CCFF00]/20">
                <h2 className="text-2xl font-bold mb-4">Get Your Personalized {workout.title.replace(' Programs', '')} Plan</h2>
                <p className="text-zinc-400 mb-6">AI analyzes 47+ data points to create YOUR perfect program</p>
                <Button onClick={() => navigate('/register')} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold rounded-full px-8 py-6 text-lg">
                  Start 7-Day Free Trial <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            </motion.div>
          </main>
        </div>
      </>
    );
  }

  // Index page listing all workout types
  return (
    <>
      <SEO 
        title="Workout Programs - All Training Types"
        description="Browse all VYTA AI-powered workout programs: weight loss, muscle building, marathon training, Hyrox, CrossFit, home workouts, and more. Find your perfect program."
        keywords="workout programs, fitness programs, training plans, exercise programs, fitness app, AI workout, personalized training"
        url="https://vyta.com/programs"
      />
      <div className="min-h-screen bg-black text-white">
        <header className="border-b border-zinc-800">
          <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-[#CCFF00] flex items-center justify-center">
                <span className="text-black font-black text-xl">V</span>
              </div>
              <span className="font-bold text-xl">VYTA</span>
            </Link>
            <Button onClick={() => navigate('/register')} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold rounded-full">
              Start Free Trial
            </Button>
          </div>
        </header>

        <main className="max-w-6xl mx-auto px-6 py-16">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-16">
            <h1 className="text-4xl sm:text-5xl font-black mb-4">AI-Powered <span className="text-[#CCFF00]">Workout Programs</span></h1>
            <p className="text-xl text-zinc-400">Find your perfect training program, personalized by AI</p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {Object.entries(workoutTypes).map(([key, workout], index) => (
              <motion.div
                key={key}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Link
                  to={`/programs/${key}`}
                  className="block p-6 rounded-2xl bg-zinc-900/50 border border-zinc-800 hover:border-[#CCFF00]/50 transition-colors"
                >
                  <h2 className="text-xl font-bold mb-2">{workout.title}</h2>
                  <p className="text-sm text-zinc-400 line-clamp-2">{workout.description}</p>
                  <span className="inline-flex items-center gap-1 mt-4 text-[#CCFF00] text-sm font-medium">
                    Learn more <ArrowRight className="w-4 h-4" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </main>
      </div>
    </>
  );
}
